import { modelHelperModule } from './model-helper';
import { assessmentWorkflowPathModelModule } from './assessment-workflow-path';
import * as ArrayUtils from '../utils/array-utils';
import { appConfig } from '../constants/app-config';
import { compareIgnoreCase } from '../utils/string-utils';
import { getTeam } from '../utils/account-utils/account-utils';

export const ASSESSMENTS_STATE_ORDER = {
  published: 0,
  draft: 1,
  archived: 3,
};

const defaultWorkflowProperties = [
  'submissionMessage',
  'redirectUrl',
  'assignmentTeamMemberIds',
  'assignmentLabels',
  'webhookUrl',
];

export const MAX_RECOMMENDED_ASSESSMENT_TIME = 240; // 4 hours
export const VERY_LONG_ASSESSMENT_TIME = 480; // 8 hours
export const MAX_RECOMMENDED_NUMBER_OF_CHALLENGES = 10;
export const VERY_LONG_NUMBER_OF_CHALLENGES = 20;

export const assessmentModelModule = angular
  .module('Q.models.assessment', [
    modelHelperModule,
    assessmentWorkflowPathModelModule,
  ])

  .factory(
    'Assessment',
    function (
      ModelHelper,
      $window,
      $q,
      DS,
      AssessmentWorkflowHelper,
      $injector,
    ) {
      const stateNameMappings = {
        draft: 'Draft',
        published: 'Published',
        archived: 'Archived',
      };

      const Assessment = ModelHelper.defineResource({
        name: 'Assessment',
        endpoint: 'assessments',

        serialize(resource, data) {
          if (data.workflowPaths && data.workflowPaths.length) {
            // We need to remove any disabled workflows before serializing
            data.workflowPaths = data.workflowPaths.filter(
              (wf) => wf.$enabled !== false,
            );
          }
          return ModelHelper.serialize(resource, data);
        },

        beforeInject(resource, data) {
          if (!Array.isArray(data)) data = [data];
          data.forEach((d) => {
            // denormalize any default workflow actions
            d.$defaultWorkflowActions = AssessmentWorkflowHelper.actions(d);
          });
        },

        computed: {
          selectTitle: /* @ngInject */ function (title, demoData) {
            return title + (demoData ? ' (DEMO)' : '');
          },
          isSample: /* @ngInject */ function (isPractice, isDemo) {
            return isPractice || isDemo;
          },
          difficulty: {
            get() {
              if (this.$difficulty === undefined) {
                const difficulties = this.challenges().map(
                  (challenge) => +challenge.difficulty || 0,
                );
                this.$difficulty = Math.max(...difficulties);
              }
              return this.$difficulty;
            },
          },
        },

        methods: {
          challenges() {
            if (this.digest && this.digest.challengeStats)
              return this.digest.challengeStats;
            if (!this.challengeItems || !this.challengeItems.length) return [];
            return this.challengeItems.reduce((arr, ci) => {
              if (ci.challenge) {
                arr.push(ci.challenge);
              } else {
                // load missing challenge for next time, but don't include it right now
                ci.getChallenge();
              }
              return arr;
            }, []);
          },
          topics() {
            const allTopics = this.challenges().reduce(
              (arr, challenge) => arr.concat(challenge.topics || []),
              [],
            );
            return ArrayUtils.unique(allTopics);
          },
          stateName() {
            return stateNameMappings[this.state];
          },
          assessUrl(setupMode) {
            return (
              $window.location.origin +
              appConfig.assessRoute +
              '/' +
              this.id +
              (setupMode ? '?fromSetup=true' : '')
            );
          },
          isDraft() {
            return this.state === 'draft';
          },
          isArchived() {
            return this.state === 'archived';
          },
          isPublished() {
            return this.state === 'published';
          },
          isPreviewable() {
            return (
              !this.demoData &&
              (this.isPublished() ||
                (this.challengeItems && this.challengeItems.length > 0))
            );
          },
          isSoftDeleted() {
            return !!this.softDeletedAt;
          },
          estimatedTime() {
            return this.manualEstimatedTime || this.suggestedEstimatedTime;
          },
          checkIfOverLong(challenge) {
            const assessment = this;

            const team = getTeam($injector) || assessment.team;
            if (team && !team.warnOnLongAssessments) return false;

            const totalChallengeCount =
              assessment.challengeItems.length + (challenge ? 1 : 0);
            const tooManyChallenges =
              totalChallengeCount > MAX_RECOMMENDED_NUMBER_OF_CHALLENGES;

            let baseTime = assessment.estimatedTime();
            if (assessment.challengeItems.every((c) => c.challenge)) {
              // use internally estimated time for challenges;
              baseTime = assessment.challengeItems.reduce(
                (s, ci) => s + ci.challenge.expectedTime(),
                0,
              );
            }
            const totalTime =
              baseTime + (challenge ? challenge.expectedTime() : 0);
            // only warn on too long if there's 2 or more challenges, in case there's one really long challenge.
            const tooLong =
              totalChallengeCount > 1 &&
              totalTime > MAX_RECOMMENDED_ASSESSMENT_TIME;

            const veryLong =
              totalTime > VERY_LONG_ASSESSMENT_TIME ||
              totalChallengeCount > VERY_LONG_NUMBER_OF_CHALLENGES;

            if (tooLong || tooManyChallenges) {
              return {
                veryLong,
                tooManyChallenges,
                totalChallengeCount,
                tooLong,
                totalTime,
              };
            } else {
              return false;
            }
          },
          hasTimedChallenges() {
            return this.challenges().some((c) => !c.isUntimed());
          },
          trackProperties: function () {
            return {
              title: this.title,
              id: this.id,
              teamId: this.teamId,
              state: this.state,
              summary: this.summary,
              availablePositions: this.availablePositions,
            };
          },
          loadCopiedFrom: function () {
            if (
              !this.$copiedFromLoaded &&
              !this.copiedFrom &&
              this.copiedFromId
            ) {
              // HACK: because templates and normal assessments are the same model,
              // we have to look up the template and inject it manually
              return Assessment.find(this.copiedFromId, {
                cacheResponse: false,
              })
                .then((a) => {
                  return DS.inject('AssessmentTemplate', a);
                })
                .catch(() => null)
                .finally(() => {
                  // to prevent reloading errors repeatedly
                  this.$copiedFromLoaded = true;
                });
            } else {
              return $q.resolve(this.copiedFrom);
            }
          },
          difficultyName() {
            return (
              DS.definitions.Challenge.difficulties[this.difficulty] || 'Warmup'
            );
          },
          templateAssessment() {
            if (this.copiedFrom) {
              const template = this.copiedFrom;
              if (template.isTemplate) {
                return template;
              }
            }
            return null;
          },
          hasStats(type) {
            const stats = this[type + 'Stats'];
            const globalStats = this.globalStats(type);
            // ignore if only one result
            return (
              !!(stats && stats.count > 1) ||
              !!(globalStats && globalStats.count > 1)
            );
          },
          globalStats(type) {
            const template = this.templateAssessment();
            return template && template[type + 'Stats'];
          },
          proctoringEnabled() {
            const team = this.team;
            return (
              team &&
              team.hasFeature('proctoring') &&
              (this.enableProctoring === 'enabled' ||
                (this.enableProctoring === 'default' &&
                  team.defaultEnableProctoring))
            );
          },

          // workflow methods
          getSimpleWorkflow(type, createIfNotFound) {
            const workflowType = 'simple_' + type;
            let wfp = this.workflowPaths;
            if (!wfp) {
              wfp = this.workflowPaths = [];
            }
            let wf = wfp.find((wf) => wf.workflowType === workflowType);
            if (!wf && createIfNotFound) {
              wf = DS.createInstance('AssessmentWorkflowPath', {
                assessmentId: this.id,
                order: wfp.length,
                workflowType: workflowType,
                timeType: 'accumulated',
              });
              wfp.push(wf);
            }
            return wf;
          },

          hasCustomWorkflow() {
            return (
              this.workflowPaths &&
              this.workflowPaths.some((wf) => !wf.workflowType)
            );
          },

          hasDefaultWorkflow() {
            return defaultWorkflowProperties.some((k) => {
              const prop = this[k];
              return Array.isArray(prop) ? prop.length : !!prop;
            });
          },

          clearDefaultWorkflow() {
            defaultWorkflowProperties.forEach((k) => {
              this[k] = Array.isArray(this[k]) ? [] : null;
              this[`$${k}Enabled`] = false;
            });
          },

          // default workflow actions
          submissionMessageEnabled:
            AssessmentWorkflowHelper.actionEnabledString('submissionMessage'),
          assignmentTeamMemberIdsEnabled:
            AssessmentWorkflowHelper.actionEnabledArray(
              'assignmentTeamMemberIds',
            ),
          assignmentLabelsEnabled:
            AssessmentWorkflowHelper.actionEnabledArray('assignmentLabels'),
          redirectUrlEnabled:
            AssessmentWorkflowHelper.actionEnabledString('redirectUrl'),
          webhookUrlEnabled:
            AssessmentWorkflowHelper.actionEnabledString('webhookUrl'),
        },

        relations: {
          hasMany: {
            AssessmentResult: {
              localField: 'assessmentResults',
              foreignKey: 'assessmentId',
            },
            AssessmentCohort: {
              localField: 'cohorts',
              foreignKey: 'assessmentId',
            },
            ChallengeItem: {
              localField: 'challengeItems',
              link: false,
            },
            AssessmentWorkflowPath: {
              localField: 'workflowPaths',
              foreignKey: 'assessmentId',
              link: false,
            },
            TeamMember: [
              {
                localField: 'assignmentTeamMembers',
                localKeys: 'assignmentTeamMemberIds',
              },
              {
                localField: 'defaultAssignedTeamMembers',
                localKeys: 'defaultAssignedTeamMemberIds',
              },
            ],
          },
          belongsTo: {
            Team: {
              localField: 'team',
              localKey: 'teamId',
            },
            TeamMember: [
              {
                localField: 'createdBy',
                localKey: 'createdById',
              },
            ],
          },
          hasOne: {
            AssessmentTemplate: {
              localField: 'copiedFrom',
              localKey: 'copiedFromId',
            },
          },
        },

        beforeEject(resource, instances) {
          if (!Array.isArray(instances)) instances = [instances];
          instances.forEach((instance) => {
            if (instance) {
              if (instance.challengeItems) {
                instance.challengeItems.forEach((ci) => ci.DSEject());
              }
              if (instance.assessmentWorkflowPaths) {
                instance.assessmentWorkflowPaths.forEach((awp) =>
                  awp.DSEject(),
                );
              }
            }
          });
        },

        meta: {
          hasDigest: true,
          cleanInjected(instance) {
            delete instance.$difficulty;
          },
          includeOnPull: ['digest'],
          saveExcludes: ['team'],
          actionEndpoints: {
            publish: { method: 'PUT', track: true },
            archive: { method: 'PUT', track: true },
            unarchive: { method: 'PUT', track: true },
            unlockAll: { method: 'PUT', track: true },
            export: { method: 'POST', track: true, cacheResponse: false },
            recover: {
              method: 'PUT',
              track: true,
              onSuccess(model) {
                // clean up local model
                delete model.softDeletedAt;
                delete model.softDeletedBy;
              },
            },
          },
          track: {
            create: true,
            update: true,
          },
        },
      });

      ModelHelper.defineQuickList(
        Assessment,
        {},
        {
          sort(a, b) {
            return (
              ASSESSMENTS_STATE_ORDER[a.state] -
                ASSESSMENTS_STATE_ORDER[b.state] ||
              compareIgnoreCase(a.title, b.title)
            );
          },
        },
      );

      return Assessment;
    },
  );
